import angular from 'angular'
import type { ModalFactory } from '../vend-ui/types'

import erroredSalesTemplate from '../components/sale/errored-sales-modal/errored-sales-modal.html'
import { $q } from '../ng-compat/imports'
import { Sale } from '../models/sale'
import { UserManager } from '../managers/user-manager'
import { WEB_REGISTER } from '../components/web-register.constant'
import { SaleSyncManager } from '../managers/sale-sync-manager'
import { RetailerSettingsManager } from '../managers/retailer-settings-manager'

const SALE_MODALS_CONFIG = {
  CONTEXTS: {
    DEFAULT: 'DEFAULT',
    SWITCH_REGISTERS: 'SWITCH_REGISTERS',
    TRAINING_MODE: 'TRAINING_MODE',
    CLOSE_REGISTER: 'CLOSE_REGISTER',
  },
}

function SaleModalsFactory(
  Modal: ModalFactory,
  saleSyncManager: SaleSyncManager,
  retailerSettingsManager: RetailerSettingsManager,
  userManager: UserManager
) {
  /**
   * Provides sale related modals e.g. to action an existing sale while loading another.
   *
   * @class SaleModals
   * @static
   */
  return {
    /**
     * The available contexts for sale modals.
     *
     * @readOnly
     * @property CONTEXTS
     * @type Object
     */
    get CONTEXTS() {
      return SALE_MODALS_CONFIG.CONTEXTS
    },

    /**
     * Display the errored sales modal.
     *
     * @method viewErroredSales
     *
     * @return {Modal<Promise>} The Modal instance
     */
    viewErroredSales() {
      const retailerSettings = retailerSettingsManager.get()
      const registerData = {
        clientVersion: WEB_REGISTER.CLIENT_VERSION,
        domainPrefix: retailerSettings.domain_prefix,
        retailerId: retailerSettings.id,
        userId: userManager.getLoggedInUser().id,
      }

      return $q
        .all({
          erroredSales: saleSyncManager.getErroredSales(),
          erroredSalesFileUrl: saleSyncManager.generateErroredSalesFile(
            registerData
          ),
        })
        .then(({ erroredSales, erroredSalesFileUrl }) => {
          return this._erroredSalesModal(erroredSales, erroredSalesFileUrl)
        })
        .then(modal => modal.closed)
        .then(() => saleSyncManager.clearErroredSalesFile())
    },

    /**
     * Construct the errored sales modal.
     *
     * @private
     * @method _erroredSalesModal
     *
     * @param {Array<Sale>} erroredSales
     *        Collection of errored sales
     *
     * @param {String} erroredSalesFileUrl
     *        The download url for the errored sales
     */
    _erroredSalesModal(erroredSales: Sale[], erroredSalesFileUrl: string) {
      return Modal({
        template: erroredSalesTemplate,
        controller: 'ErroredSalesModalController',
        controllerAs: '$ctrl',
        erroredSales,
        erroredSalesFileUrl,
      })
    },
  }
}

export type SaleModals = ReturnType<typeof SaleModalsFactory>

/**
 * Provides sale related modals e.g. to action an existing sale while loading another.
 *
 * @module saleModals
 */
angular.module('webregister.modals').factory('saleModals', SaleModalsFactory)
